import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, useLanguage, API_URL } from '../context/AppContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import axios from 'axios';
import {
  Users, BookOpen, GraduationCap, Building2, MessageSquare, Bell,
  Calendar, Clock, FileText, CreditCard, Award, Target, Sparkles,
  Layers, ClipboardList, Bus, Library, UserCheck, ChevronRight,
  DollarSign, Shield, Briefcase, BookMarked, BarChart3, Eye,
  TrendingUp, UserPlus, ArrowUpRight, CheckCircle2, XCircle, AlertCircle,
  Megaphone, FolderOpen, UserCircle
} from 'lucide-react';

export const DashboardPage = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const fr = language === 'fr';

  useEffect(() => {
    fetchDashboardStats();
    const interval = setInterval(fetchDashboardStats, 20000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/stats`);
      setStats(response.data);
    } catch (error) { console.error('Error fetching stats:', error); }
    finally { setLoading(false); }
  };

  const role = user?.role;

  if (loading) return (
    <div className="space-y-6 animate-pulse" data-testid="dashboard-loading">
      <div className="h-20 bg-white rounded-2xl" />
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">{[1,2,3,4,5,6].map(i => <div key={i} className="h-32 bg-white rounded-2xl" />)}</div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4"><div className="h-40 bg-white rounded-2xl" /><div className="h-40 bg-white rounded-2xl" /></div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Welcome Header */}
      <div>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900" data-testid="welcome-header">
          {fr ? 'Bienvenue' : 'Welcome'}, {user?.first_name}
        </h1>
        <p className="text-slate-400 text-sm mt-1 capitalize">{getRoleLabel(role, fr)}</p>
      </div>

      {(role === 'super_admin' || role === 'co_admin') && <SuperAdminDash stats={stats} fr={fr} role={role} />}
      {['rector','vice_rector_academic','sg_academic','sg_admin','sg_research','budget_director'].includes(role) && <RectorDash stats={stats} fr={fr} role={role} />}
      {['dean','vice_dean','faculty_secretary'].includes(role) && <DeanDash stats={stats} fr={fr} />}
      {['department_head','section_head','dept_secretary'].includes(role) && <DeptDash stats={stats} fr={fr} />}
      {['teacher','visiting_teacher'].includes(role) && <TeacherDash stats={stats} fr={fr} />}
      {role === 'student' && <StudentDash stats={stats} fr={fr} />}
      {role === 'driver' && <DriverDash stats={stats} fr={fr} />}
      {['admin_staff','academic_secretary','registrar','cashier','librarian','it_admin','apparitor','protocol_chief','receptionist'].includes(role) && <StaffDash stats={stats} fr={fr} role={role} />}
    </div>
  );
};

// ===== ROLE LABEL =====
const getRoleLabel = (role, fr) => {
  const labels = {
    super_admin: 'Super Administrateur', co_admin: 'Co-Administrateur', rector: 'Recteur',
    vice_rector_academic: 'Vice-Recteur Academique', sg_academic: 'SG Academique',
    sg_admin: 'SG Administratif', sg_research: 'SG Recherche', budget_director: 'Directeur Budget',
    dean: 'Doyen', vice_dean: 'Vice-Doyen', faculty_secretary: 'Secretaire Fac.',
    department_head: 'Chef Departement', section_head: 'Chef Section', dept_secretary: 'Secretaire Dept.',
    teacher: 'Enseignant', visiting_teacher: 'Enseignant Visiteur',
    admin_staff: 'Personnel Admin.', academic_secretary: 'Secretaire Acad.',
    registrar: 'Charge Inscriptions', cashier: 'Caissier', librarian: 'Bibliothecaire',
    it_admin: 'Informaticien', apparitor: 'Appariteur', protocol_chief: 'Chef Protocole',
    receptionist: 'Agent d\'accueil', student: 'Etudiant', driver: 'Chauffeur',
  };
  return labels[role] || role;
};

// ===== STAT CARD =====
const StatCard = ({ icon: Icon, label, value, color = 'green', link }) => {
  const colors = {
    green: 'bg-emerald-50 text-emerald-600',
    orange: 'bg-orange-50 text-orange-600',
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-violet-50 text-violet-600',
    red: 'bg-red-50 text-red-600',
    amber: 'bg-amber-50 text-amber-600',
    cyan: 'bg-cyan-50 text-cyan-600',
    slate: 'bg-slate-100 text-slate-600',
    teal: 'bg-teal-50 text-teal-600',
  };
  const c = colors[color] || colors.green;
  const content = (
    <div className="bg-white rounded-2xl p-5 hover:shadow-md transition-all border border-slate-100" data-testid={`stat-${label?.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className={`w-11 h-11 rounded-xl ${c} flex items-center justify-center mb-4`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-3xl font-heading font-bold text-slate-900">{typeof value === 'number' ? value.toLocaleString() : (value ?? 0)}</p>
      <p className="text-sm text-slate-400 mt-1">{label}</p>
      {link && <p className="text-xs text-slate-400 mt-2 flex items-center gap-1 hover:text-slate-600">Voir <ChevronRight className="w-3 h-3" /></p>}
    </div>
  );
  return link ? <Link to={link}>{content}</Link> : content;
};

// ===== SPLIT CARD =====
const SplitCard = ({ icon: Icon, title, sections, link }) => (
  <div className="bg-white rounded-2xl p-5 border border-slate-100">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-5 h-5 text-slate-500" />}
        <h3 className="font-heading font-semibold text-slate-800 text-sm">{title}</h3>
      </div>
      {link && <Link to={link}><ArrowUpRight className="w-4 h-4 text-slate-400 hover:text-slate-600" /></Link>}
    </div>
    <div className={`grid grid-cols-${sections.length} gap-3`}>
      {sections.map((s, i) => (
        <div key={i} className="bg-slate-50 rounded-xl p-3 text-center">
          <p className={`text-2xl font-heading font-bold ${s.color || 'text-slate-900'}`}>{s.value}</p>
          <p className="text-xs text-slate-400 mt-1">{s.label}</p>
        </div>
      ))}
    </div>
  </div>
);

// ===== QUICK ACTION PILLS =====
const QuickActionBar = ({ actions }) => (
  <div className="flex gap-2 flex-wrap" data-testid="quick-actions">
    {actions.map((a, i) => (
      <Link key={i} to={a.href} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold text-white transition-all hover:opacity-90 hover:shadow-md ${a.bg}`}>
        <a.icon className="w-4 h-4" /> {a.label}
      </Link>
    ))}
  </div>
);

// ===== SECTION CARD =====
const SectionCard = ({ title, link, children }) => (
  <div className="bg-white rounded-2xl p-5 border border-slate-100">
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-heading font-semibold text-slate-800 text-sm">{title}</h3>
      {link && <Link to={link} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1">Voir <ChevronRight className="w-3 h-3" /></Link>}
    </div>
    {children}
  </div>
);

// ===== ANNOUNCEMENTS LIST =====
const AnnouncementsList = ({ announcements, fr }) => {
  if (!announcements || announcements.length === 0) {
    return <p className="text-sm text-slate-400 text-center py-4">{fr ? 'Aucune annonce recente' : 'No recent announcements'}</p>;
  }
  const typeColors = { urgent: 'bg-red-100 text-red-700', info: 'bg-blue-100 text-blue-700', announcement: 'bg-emerald-100 text-emerald-700', schedule_change: 'bg-amber-100 text-amber-700' };
  const scopeLabels = { institution: 'Institution', faculty: 'Faculte', department: 'Dept.', class: 'Classe', course: 'Cours' };
  return (
    <div className="space-y-3">
      {announcements.map((a, i) => (
        <div key={a.id || i} className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
          <Megaphone className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-700 truncate">{a.title}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${typeColors[a.type] || typeColors.announcement}`}>{a.type}</span>
              <span className="text-[10px] text-slate-400">{scopeLabels[a.scope_type] || a.scope_type}</span>
              {a.author_name && <span className="text-[10px] text-slate-400">- {a.author_name}</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ===== COURSE TABLE =====
const CourseTable = ({ courses, fr, showGrade, showTeacher, showStudents }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-slate-100">
          <th className="text-left py-2 px-3 text-xs font-semibold text-slate-400">{fr ? 'Cours' : 'Course'}</th>
          <th className="text-left py-2 px-3 text-xs font-semibold text-slate-400">Code</th>
          <th className="text-center py-2 px-3 text-xs font-semibold text-slate-400">Credits</th>
          {showTeacher && <th className="text-left py-2 px-3 text-xs font-semibold text-slate-400">{fr ? 'Enseignant' : 'Teacher'}</th>}
          {showStudents && <th className="text-center py-2 px-3 text-xs font-semibold text-slate-400">{fr ? 'Etud.' : 'Stud.'}</th>}
          {showGrade && <th className="text-center py-2 px-3 text-xs font-semibold text-slate-400">{fr ? 'Note' : 'Grade'}</th>}
        </tr>
      </thead>
      <tbody>
        {(!courses || courses.length === 0) ? (
          <tr><td colSpan={6} className="text-center py-6 text-slate-400">{fr ? 'Aucun cours' : 'No courses'}</td></tr>
        ) : courses.map(c => (
          <tr key={c.id} className="border-b border-slate-50 hover:bg-slate-50/50">
            <td className="py-3 px-3 font-medium text-slate-700">{c.name}</td>
            <td className="py-3 px-3 text-slate-500 font-mono text-xs">{c.code}</td>
            <td className="py-3 px-3 text-center text-slate-600">{c.credits}</td>
            {showTeacher && <td className="py-3 px-3 text-slate-500">{c.teacher_name || '--'}</td>}
            {showStudents && <td className="py-3 px-3 text-center text-slate-600">{c.student_count ?? '--'}</td>}
            {showGrade && (
              <td className="py-3 px-3 text-center">
                <span className={`font-semibold ${c.grade != null ? (c.grade >= 10 ? 'text-emerald-600' : 'text-red-500') : 'text-slate-300'}`}>
                  {c.grade != null ? `${c.grade}/${c.max_score}` : '--'}
                </span>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ============================================================================
// SUPER ADMIN / CO-ADMIN DASHBOARD
// ============================================================================
const SuperAdminDash = ({ stats, fr, role }) => (
  <>
    {/* Live Activity */}
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading font-bold text-slate-800">{fr ? 'Activite en direct' : 'Live activity'}</h2>
        <span className="text-xs text-slate-400 flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />{fr ? 'Mise a jour automatique' : 'Auto refresh'}</span>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Bell} label={fr ? 'Notifications' : 'Notifications'} value={stats.unread_notifications || 0} color="green" link="/notifications" />
        <StatCard icon={MessageSquare} label={fr ? 'Messages non lus' : 'Unread messages'} value={stats.unread_messages || 0} color="blue" link="/messages" />
        <StatCard icon={Megaphone} label={fr ? 'Annonces totales' : 'Total announcements'} value={stats.total_announcements || 0} color="amber" link="/announcements" />
        <StatCard icon={FolderOpen} label={fr ? 'Demandes en attente' : 'Pending requests'} value={stats.pending_doc_requests || 0} color="red" />
      </div>
    </div>

    {/* Main Stats */}
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
      <StatCard icon={Building2} label={fr ? 'Total Universites' : 'Total Universities'} value={stats.universities} color="green" link="/universities" />
      <StatCard icon={GraduationCap} label={fr ? 'Total Etudiants' : 'Total Students'} value={stats.total_students} color="orange" link="/users" />
      <StatCard icon={Users} label={fr ? 'Total Enseignants' : 'Total Teachers'} value={stats.total_teachers} color="purple" link="/users" />
      <StatCard icon={BookOpen} label={fr ? 'Total Cours' : 'Total Courses'} value={stats.total_courses} color="blue" link="/courses" />
      <StatCard icon={Layers} label={fr ? 'Groupes de Classe' : 'Class Groups'} value={stats.total_class_groups || 0} color="teal" link="/class-groups" />
      <StatCard icon={CreditCard} label={fr ? 'Inscriptions en attente' : 'Pending Enrollments'} value={stats.pending_registrations || 0} color="red" link="/registrations" />
    </div>

    {/* Revenue / Activity / Enrollments */}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <SplitCard icon={DollarSign} title={fr ? 'Revenus' : 'Revenue'} link="/payments" sections={[
        { value: '$0', label: fr ? 'Encaisses' : 'Collected', color: 'text-emerald-600' },
        { value: '$0', label: fr ? 'En attente' : 'Pending', color: 'text-red-500' },
      ]} />
      <SplitCard icon={BarChart3} title={fr ? 'Activite (30 jours)' : 'Activity (30 days)'} link="/analytics" sections={[
        { value: stats.total_students || 0, label: fr ? 'Etudiants' : 'Students' },
        { value: stats.total_courses || 0, label: fr ? 'Cours' : 'Courses' },
        { value: stats.total_users || 0, label: fr ? 'Utilisateurs' : 'Users' },
      ]} />
      <SplitCard icon={UserPlus} title={fr ? 'Inscriptions en ligne' : 'Online Enrollments'} link="/registrations" sections={[
        { value: stats.pending_registrations || 0, label: fr ? 'En attente' : 'Pending', color: 'text-red-500' },
        { value: 0, label: fr ? 'Acceptees' : 'Accepted', color: 'text-emerald-600' },
      ]} />
    </div>

    {/* Quick Actions */}
    <QuickActionBar actions={[
      { icon: Building2, label: fr ? 'Universites' : 'Universities', href: '/universities', bg: 'bg-slate-800' },
      { icon: GraduationCap, label: fr ? 'Inscriptions' : 'Enrollments', href: '/registrations', bg: 'bg-orange-500' },
      { icon: Shield, label: 'Co-admins', href: '/co-admins', bg: 'bg-blue-600' },
      { icon: Layers, label: fr ? 'Classes' : 'Classes', href: '/class-groups', bg: 'bg-teal-600' },
      { icon: Megaphone, label: fr ? 'Annonces' : 'Announcements', href: '/announcements', bg: 'bg-amber-600' },
      { icon: BarChart3, label: 'Analytics', href: '/analytics', bg: 'bg-emerald-600' },
      { icon: MessageSquare, label: 'Messages', href: '/messages', bg: 'bg-slate-500' },
    ]} />

    {/* Recent Announcements */}
    {stats.recent_announcements?.length > 0 && (
      <SectionCard title={fr ? 'Annonces recentes' : 'Recent announcements'} link="/announcements">
        <AnnouncementsList announcements={stats.recent_announcements} fr={fr} />
      </SectionCard>
    )}
  </>
);

// ============================================================================
// RECTOR / UNIVERSITY MANAGEMENT DASHBOARD
// ============================================================================
const RectorDash = ({ stats, fr, role }) => (
  <>
    {/* Stat Cards Row */}
    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
      <StatCard icon={GraduationCap} label={fr ? 'Total Etudiants' : 'Total Students'} value={stats.students} color="green" link="/users" />
      <StatCard icon={Users} label={fr ? 'Total Enseignants' : 'Total Teachers'} value={stats.teachers} color="orange" link="/users" />
      <StatCard icon={BookOpen} label={fr ? 'Total Cours' : 'Total Courses'} value={stats.courses} color="red" link="/courses" />
      <StatCard icon={Layers} label={fr ? 'Groupes Classes' : 'Class Groups'} value={stats.class_groups || 0} color="teal" link="/class-groups" />
      <StatCard icon={Megaphone} label={fr ? 'Annonces' : 'Announcements'} value={stats.announcements_count || 0} color="blue" link="/announcements" />
      <StatCard icon={FolderOpen} label={fr ? 'Demandes docs' : 'Doc requests'} value={stats.pending_doc_requests || 0} color="amber" />
    </div>

    {/* Presences + Paiements */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <SplitCard icon={CheckCircle2} title={fr ? 'Presences du jour' : 'Today\'s Attendance'} sections={[
        { value: 0, label: fr ? 'Presents' : 'Present', color: 'text-emerald-600' },
        { value: 0, label: fr ? 'Absents' : 'Absent', color: 'text-red-500' },
        { value: stats.students || 0, label: 'Total' },
      ]} />
      <SplitCard icon={CreditCard} title={fr ? 'Paiements' : 'Payments'} link="/payments" sections={[
        { value: `$${(stats.total_revenue || 0).toLocaleString()}`, label: fr ? 'Encaisses' : 'Collected', color: 'text-emerald-600' },
        { value: '$0', label: fr ? 'En attente' : 'Pending', color: 'text-red-500' },
      ]} />
    </div>

    {/* Quick Actions */}
    <QuickActionBar actions={[
      { icon: GraduationCap, label: fr ? 'Etudiants' : 'Students', href: '/users', bg: 'bg-emerald-600' },
      { icon: BarChart3, label: fr ? 'Notes' : 'Grades', href: '/grades', bg: 'bg-orange-500' },
      { icon: Layers, label: fr ? 'Classes' : 'Classes', href: '/class-groups', bg: 'bg-teal-600' },
      { icon: Megaphone, label: fr ? 'Annonces' : 'Announcements', href: '/announcements', bg: 'bg-amber-600' },
      { icon: Calendar, label: fr ? 'Emploi du Temps' : 'Schedule', href: '/schedule', bg: 'bg-blue-600' },
      { icon: FileText, label: fr ? 'Bulletins' : 'Report Cards', href: '/deliberations', bg: 'bg-slate-800' },
      { icon: MessageSquare, label: 'Messages', href: '/messages', bg: 'bg-violet-600' },
    ]} />

    {/* Faculties Table */}
    {stats.faculties_list?.length > 0 && (
      <SectionCard title={fr ? 'Detail des facultes' : 'Faculty Details'} link="/faculties">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left py-2 px-3 text-xs font-semibold text-slate-400">{fr ? 'Faculte' : 'Faculty'}</th>
                <th className="text-center py-2 px-3 text-xs font-semibold text-slate-400">{fr ? 'Departements' : 'Departments'}</th>
                <th className="text-center py-2 px-3 text-xs font-semibold text-slate-400">{fr ? 'Etudiants' : 'Students'}</th>
              </tr>
            </thead>
            <tbody>
              {stats.faculties_list.map(f => (
                <tr key={f.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="py-3 px-3 font-medium text-slate-700">{f.name}</td>
                  <td className="py-3 px-3 text-center text-slate-600">{f.dept_count}</td>
                  <td className="py-3 px-3 text-center text-slate-600">{f.student_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    )}

    {/* Recent Announcements */}
    {stats.recent_announcements?.length > 0 && (
      <SectionCard title={fr ? 'Annonces recentes' : 'Recent announcements'} link="/announcements">
        <AnnouncementsList announcements={stats.recent_announcements} fr={fr} />
      </SectionCard>
    )}

    {/* Recent Courses */}
    {stats.recent_courses?.length > 0 && (
      <SectionCard title={fr ? 'Cours recents' : 'Recent courses'} link="/courses">
        <CourseTable courses={stats.recent_courses} fr={fr} showTeacher />
      </SectionCard>
    )}
  </>
);

// ============================================================================
// DEAN / VICE-DEAN DASHBOARD
// ============================================================================
const DeanDash = ({ stats, fr }) => (
  <>
    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
      <StatCard icon={Layers} label={fr ? 'Departements' : 'Departments'} value={stats.departments} color="purple" link="/departments" />
      <StatCard icon={BookMarked} label={fr ? 'Programmes' : 'Programs'} value={stats.programs} color="blue" link="/programs" />
      <StatCard icon={GraduationCap} label={fr ? 'Etudiants' : 'Students'} value={stats.students} color="green" />
      <StatCard icon={BookOpen} label={fr ? 'Cours' : 'Courses'} value={stats.courses} color="orange" link="/courses" />
      <StatCard icon={Layers} label={fr ? 'Groupes Classes' : 'Class Groups'} value={stats.class_groups || 0} color="teal" link="/class-groups" />
      <StatCard icon={Megaphone} label={fr ? 'Annonces' : 'Announcements'} value={stats.announcements_count || 0} color="amber" link="/announcements" />
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <SplitCard icon={CheckCircle2} title={fr ? 'Presences du jour' : 'Attendance'} sections={[
        { value: 0, label: fr ? 'Presents' : 'Present', color: 'text-emerald-600' },
        { value: 0, label: fr ? 'Absents' : 'Absent', color: 'text-red-500' },
        { value: stats.students || 0, label: 'Total' },
      ]} />
      <SplitCard icon={CreditCard} title={fr ? 'Paiements' : 'Payments'} sections={[
        { value: '$0', label: fr ? 'Encaisses' : 'Collected', color: 'text-emerald-600' },
        { value: '$0', label: fr ? 'En attente' : 'Pending', color: 'text-red-500' },
      ]} />
    </div>
    <QuickActionBar actions={[
      { icon: BookOpen, label: fr ? 'Cours' : 'Courses', href: '/courses', bg: 'bg-emerald-600' },
      { icon: BarChart3, label: fr ? 'Notes' : 'Grades', href: '/grades', bg: 'bg-orange-500' },
      { icon: Layers, label: fr ? 'Classes' : 'Classes', href: '/class-groups', bg: 'bg-teal-600' },
      { icon: Megaphone, label: fr ? 'Annonces' : 'Announcements', href: '/announcements', bg: 'bg-amber-600' },
      { icon: Award, label: fr ? 'Deliberations' : 'Deliberations', href: '/deliberations', bg: 'bg-violet-600' },
      { icon: MessageSquare, label: 'Messages', href: '/messages', bg: 'bg-blue-600' },
    ]} />

    {/* Recent Announcements */}
    {stats.recent_announcements?.length > 0 && (
      <SectionCard title={fr ? 'Annonces recentes' : 'Recent announcements'} link="/announcements">
        <AnnouncementsList announcements={stats.recent_announcements} fr={fr} />
      </SectionCard>
    )}

    {stats.recent_courses?.length > 0 && (
      <SectionCard title={fr ? 'Cours de la faculte' : 'Faculty courses'} link="/courses">
        <CourseTable courses={stats.recent_courses} fr={fr} showTeacher />
      </SectionCard>
    )}
  </>
);

// ============================================================================
// DEPARTMENT HEAD DASHBOARD
// ============================================================================
const DeptDash = ({ stats, fr }) => (
  <>
    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
      <StatCard icon={BookMarked} label={fr ? 'Programmes' : 'Programs'} value={stats.programs} color="blue" />
      <StatCard icon={BookOpen} label={fr ? 'Cours' : 'Courses'} value={stats.courses} color="orange" link="/courses" />
      <StatCard icon={GraduationCap} label={fr ? 'Etudiants' : 'Students'} value={stats.students} color="green" />
      <StatCard icon={Users} label={fr ? 'Enseignants' : 'Teachers'} value={stats.teachers} color="purple" />
      <StatCard icon={Layers} label={fr ? 'Groupes Classes' : 'Class Groups'} value={stats.class_groups || 0} color="teal" link="/class-groups" />
      <StatCard icon={Megaphone} label={fr ? 'Annonces' : 'Announcements'} value={stats.announcements_count || 0} color="amber" link="/announcements" />
    </div>
    <QuickActionBar actions={[
      { icon: BookOpen, label: fr ? 'Cours' : 'Courses', href: '/courses', bg: 'bg-emerald-600' },
      { icon: BarChart3, label: fr ? 'Notes' : 'Grades', href: '/grades', bg: 'bg-orange-500' },
      { icon: Layers, label: fr ? 'Classes' : 'Classes', href: '/class-groups', bg: 'bg-teal-600' },
      { icon: Megaphone, label: fr ? 'Annonces' : 'Announcements', href: '/announcements', bg: 'bg-amber-600' },
      { icon: Award, label: fr ? 'Deliberations' : 'Deliberations', href: '/deliberations', bg: 'bg-violet-600' },
      { icon: MessageSquare, label: 'Messages', href: '/messages', bg: 'bg-slate-600' },
    ]} />

    {/* Recent Announcements */}
    {stats.recent_announcements?.length > 0 && (
      <SectionCard title={fr ? 'Annonces recentes' : 'Recent announcements'} link="/announcements">
        <AnnouncementsList announcements={stats.recent_announcements} fr={fr} />
      </SectionCard>
    )}

    {stats.recent_courses?.length > 0 && (
      <SectionCard title={fr ? 'Cours du departement' : 'Department courses'} link="/courses">
        <CourseTable courses={stats.recent_courses} fr={fr} showTeacher />
      </SectionCard>
    )}
  </>
);

// ============================================================================
// TEACHER DASHBOARD
// ============================================================================
const TeacherDash = ({ stats, fr }) => (
  <>
    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
      <StatCard icon={BookOpen} label={fr ? 'Mes cours' : 'My courses'} value={stats.my_courses} color="green" link="/courses" />
      <StatCard icon={GraduationCap} label={fr ? 'Mes etudiants' : 'My students'} value={stats.total_students} color="orange" />
      <StatCard icon={ClipboardList} label={fr ? 'Devoirs' : 'Assignments'} value={stats.my_assignments} color="blue" link="/assignments" />
      <StatCard icon={FileText} label={fr ? 'Soumissions' : 'Submissions'} value={stats.pending_submissions} color="red" />
      <StatCard icon={Layers} label={fr ? 'Groupes Classes' : 'Class Groups'} value={stats.my_class_groups || 0} color="teal" />
      <StatCard icon={Megaphone} label={fr ? 'Annonces' : 'Announcements'} value={stats.recent_announcements?.length || 0} color="amber" link="/announcements" />
    </div>
    <QuickActionBar actions={[
      { icon: GraduationCap, label: fr ? 'Etudiants' : 'Students', href: '/users', bg: 'bg-emerald-600' },
      { icon: BarChart3, label: fr ? 'Notes' : 'Grades', href: '/grades', bg: 'bg-orange-500' },
      { icon: ClipboardList, label: fr ? 'Devoirs' : 'Assignments', href: '/assignments', bg: 'bg-blue-600' },
      { icon: Megaphone, label: fr ? 'Annonces' : 'Announcements', href: '/announcements', bg: 'bg-amber-600' },
      { icon: UserCircle, label: fr ? 'Mon Profil' : 'My Profile', href: '/teacher-profile', bg: 'bg-violet-600' },
      { icon: Calendar, label: fr ? 'Emploi du Temps' : 'Schedule', href: '/schedule', bg: 'bg-slate-800' },
      { icon: MessageSquare, label: 'Messages', href: '/messages', bg: 'bg-slate-500' },
    ]} />

    {/* Recent Announcements */}
    {stats.recent_announcements?.length > 0 && (
      <SectionCard title={fr ? 'Annonces recentes' : 'Recent announcements'} link="/announcements">
        <AnnouncementsList announcements={stats.recent_announcements} fr={fr} />
      </SectionCard>
    )}

    {stats.my_courses_list?.length > 0 && (
      <SectionCard title={fr ? 'Detail de mes cours' : 'My course details'} link="/courses">
        <CourseTable courses={stats.my_courses_list} fr={fr} showStudents />
      </SectionCard>
    )}
  </>
);

// ============================================================================
// STUDENT DASHBOARD
// ============================================================================
const StudentDash = ({ stats, fr }) => (
  <>
    {/* Program Banner */}
    {stats.program_name && (
      <div className="bg-gradient-to-r from-emerald-700 to-emerald-800 rounded-2xl p-5 text-white" data-testid="program-banner">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-emerald-200/80 text-xs font-semibold uppercase tracking-wider">{fr ? 'Mon programme' : 'My program'}</p>
            <h2 className="text-lg font-heading font-bold mt-1">{stats.program_name}</h2>
            <div className="flex items-center gap-3 mt-1 text-sm text-emerald-100/70">
              <span>{stats.promotion_label} {stats.academic_year}</span>
              {stats.faculty_name && <span>{stats.faculty_name}</span>}
            </div>
            {stats.matricule && <Badge className="bg-white/15 text-white text-xs mt-2">{stats.matricule}</Badge>}
          </div>
          <div className="text-right">
            {stats.my_class_group && (
              <div className="bg-white/10 rounded-lg px-3 py-2">
                <p className="text-emerald-200/80 text-[10px] uppercase font-semibold">{fr ? 'Ma classe' : 'My class'}</p>
                <p className="text-white font-bold text-sm">{stats.my_class_group}</p>
                {stats.is_class_leader && <Badge className="bg-amber-400/20 text-amber-200 text-[10px] mt-1">{fr ? 'Chef de classe' : 'Class leader'}</Badge>}
                {stats.is_deputy_leader && <Badge className="bg-blue-400/20 text-blue-200 text-[10px] mt-1">{fr ? 'Adjoint' : 'Deputy'}</Badge>}
              </div>
            )}
          </div>
        </div>
      </div>
    )}

    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
      <StatCard icon={BookOpen} label={fr ? 'Mes cours' : 'My courses'} value={stats.my_courses} color="green" link="/courses" />
      <StatCard icon={FileText} label={fr ? 'Mes notes' : 'My grades'} value={stats.my_grades} color="orange" link="/grades" />
      <StatCard icon={Award} label="Credits" value={`${stats.credits_earned || 0}/${stats.total_credits_required || 180}`} color="purple" />
      <StatCard icon={MessageSquare} label="Messages" value={stats.unread_messages} color="blue" link="/messages" />
      <StatCard icon={FolderOpen} label={fr ? 'Demandes docs' : 'Doc requests'} value={stats.doc_requests_total || 0} color="teal" link="/my-documents" />
      <StatCard icon={Megaphone} label={fr ? 'Annonces' : 'Announcements'} value={stats.recent_announcements?.length || 0} color="amber" link="/announcements" />
    </div>

    {/* Credits Progress + Document Requests */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="bg-white rounded-2xl p-5 border border-slate-100">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Target className="w-4 h-4 text-emerald-500" />{fr ? 'Progression credits' : 'Credit progress'}</span>
          <span className="text-sm font-bold text-slate-900">{stats.credits_earned || 0}/{stats.total_credits_required || 180}</span>
        </div>
        <Progress value={((stats.credits_earned || 0) / (stats.total_credits_required || 180)) * 100} className="h-3" />
      </div>
      <SplitCard icon={FolderOpen} title={fr ? 'Mes dossiers' : 'My documents'} link="/my-documents" sections={[
        { value: stats.doc_requests_pending || 0, label: fr ? 'En attente' : 'Pending', color: 'text-amber-600' },
        { value: stats.doc_requests_approved || 0, label: fr ? 'Approuves' : 'Approved', color: 'text-emerald-600' },
        { value: stats.doc_requests_total || 0, label: 'Total' },
      ]} />
    </div>

    <QuickActionBar actions={[
      { icon: BookOpen, label: fr ? 'Cours' : 'Courses', href: '/courses', bg: 'bg-emerald-600' },
      { icon: BarChart3, label: fr ? 'Notes' : 'Grades', href: '/grades', bg: 'bg-orange-500' },
      { icon: ClipboardList, label: fr ? 'Devoirs' : 'Assignments', href: '/assignments', bg: 'bg-blue-600' },
      { icon: Megaphone, label: fr ? 'Annonces' : 'Announcements', href: '/announcements', bg: 'bg-amber-600' },
      { icon: FolderOpen, label: fr ? 'Mes Dossiers' : 'My Documents', href: '/my-documents', bg: 'bg-teal-600' },
      { icon: Layers, label: fr ? 'Ma Classe' : 'My Class', href: '/my-class', bg: 'bg-violet-600' },
      { icon: MessageSquare, label: 'Messages', href: '/messages', bg: 'bg-slate-500' },
    ]} />

    {/* Recent Announcements */}
    {stats.recent_announcements?.length > 0 && (
      <SectionCard title={fr ? 'Annonces recentes' : 'Recent announcements'} link="/announcements">
        <AnnouncementsList announcements={stats.recent_announcements} fr={fr} />
      </SectionCard>
    )}

    {stats.my_courses_list?.length > 0 && (
      <SectionCard title={fr ? 'Detail de mes cours' : 'My course details'} link="/courses">
        <CourseTable courses={stats.my_courses_list} fr={fr} showGrade showTeacher />
      </SectionCard>
    )}
  </>
);

// ============================================================================
// DRIVER DASHBOARD
// ============================================================================
const DriverDash = ({ stats, fr }) => (
  <>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard icon={Bus} label={fr ? 'Mon bus' : 'My bus'} value={stats.my_bus?.license_plate || '--'} color="green" />
      <StatCard icon={Briefcase} label={fr ? 'Ma ligne' : 'My line'} value={stats.my_line?.name?.split(' - ')[0] || '--'} color="blue" />
      <StatCard icon={MessageSquare} label="Messages" value={stats.unread_messages} color="orange" link="/messages" />
      <StatCard icon={Bell} label="Notifications" value={stats.unread_notifications} color="purple" link="/notifications" />
    </div>
    <QuickActionBar actions={[
      { icon: Bus, label: 'Transport', href: '/transport', bg: 'bg-emerald-600' },
      { icon: MessageSquare, label: 'Messages', href: '/messages', bg: 'bg-blue-600' },
    ]} />
  </>
);

// ============================================================================
// ADMIN STAFF DASHBOARD
// ============================================================================
const StaffDash = ({ stats, fr, role }) => (
  <>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard icon={Users} label={fr ? 'Utilisateurs' : 'Users'} value={stats.total_users} color="green" link="/users" />
      <StatCard icon={GraduationCap} label={fr ? 'Etudiants' : 'Students'} value={stats.students} color="orange" />
      <StatCard icon={BookOpen} label={fr ? 'Cours' : 'Courses'} value={stats.courses} color="blue" />
      {role === 'registrar' && <StatCard icon={UserCheck} label={fr ? 'En attente' : 'Pending'} value={stats.pending_registrations} color="red" link="/registrations" />}
      {role === 'cashier' && <StatCard icon={CreditCard} label={fr ? 'Paiements' : 'Payments'} value={stats.total_payments} color="purple" link="/payments" />}
      {role === 'librarian' && <StatCard icon={Library} label={fr ? 'Ouvrages' : 'Items'} value={stats.library_items} color="purple" link="/library" />}
      {!['registrar','cashier','librarian'].includes(role) && <StatCard icon={MessageSquare} label="Messages" value={stats.unread_messages} color="purple" link="/messages" />}
    </div>
    <QuickActionBar actions={[
      ...(role === 'registrar' ? [{ icon: UserCheck, label: fr ? 'Inscriptions' : 'Registrations', href: '/registrations', bg: 'bg-emerald-600' }] : []),
      ...(role === 'cashier' ? [{ icon: CreditCard, label: fr ? 'Paiements' : 'Payments', href: '/payments', bg: 'bg-emerald-600' }] : []),
      ...(role === 'librarian' ? [{ icon: Library, label: fr ? 'Bibliotheque' : 'Library', href: '/library', bg: 'bg-emerald-600' }] : []),
      { icon: Users, label: fr ? 'Utilisateurs' : 'Users', href: '/users', bg: 'bg-slate-800' },
      { icon: MessageSquare, label: 'Messages', href: '/messages', bg: 'bg-blue-600' },
      { icon: Bell, label: 'Notifications', href: '/notifications', bg: 'bg-violet-600' },
    ]} />
  </>
);

export default DashboardPage;
